import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, TrendingDown } from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function fmtMoney(v) {
  if (v === null || v === undefined) return "—";
  return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
}

export default function ChurnDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API_BASE}/api/billing/churn`, { credentials: "include" })
      .then(async (r) => {
        if (r.status === 401) throw new Error("Sign in as the owner account to view this page.");
        if (r.status === 403) throw new Error("This page is only available to the owner account.");
        if (!r.ok) throw new Error("Could not load churn data.");
        return r.json();
      })
      .then(setData)
      .catch((e) => setError(e.message));
  }, []);

  if (error) {
    return (
      <div className="p-4" data-testid="churn-error">
        <p className="text-[13px] text-[var(--rs-text-2)]">{error}</p>
        <Link to="/account" className="text-[12px] text-[var(--rs-amber)] hover:underline">Back to Account</Link>
      </div>
    );
  }
  if (!data) return <div className="p-4 text-[12px] text-[var(--rs-text-3)]" data-testid="churn-loading">Loading churn data…</div>;

  const maxReason = Math.max(1, ...data.topReasons.map((r) => r.count));
  const maxMonth = Math.max(1, ...data.overTime.map((m) => m.count));
  const rev = data.revenue || {};
  const mom = rev.momChangePct;

  const downloadCsv = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/billing/churn/export`, { credentials: "include" });
      if (!res.ok) throw new Error("Export failed.");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "rigsentinel_churn_responses.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="p-4 space-y-6" data-testid="churn-dashboard">
      <div>
        <Link to="/account" className="inline-flex items-center gap-1 text-[11px] text-[var(--rs-text-3)] hover:text-[var(--rs-amber)]" data-testid="churn-back-link">
          <ArrowLeft className="w-3 h-3" /> Account
        </Link>
        <h1 className="font-heading font-black text-xl mt-1 flex items-center gap-2">
          <TrendingDown className="w-5 h-5" style={{ color: "var(--rs-amber)" }} />
          Churn Dashboard
        </h1>
        <p className="text-[12px] text-[var(--rs-text-3)]">
          {data.total} cancellation survey response{data.total === 1 ? "" : "s"} · owner only
        </p>
      </div>

      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="churn-revenue-snapshot">
        <div className="card-rs p-3">
          <div className="text-[10px] label-up text-[var(--rs-text-3)]">Active subscribers</div>
          <div className="text-lg font-bold mt-1" data-testid="rev-active-subs">{rev.activeSubscribers ?? "—"}</div>
        </div>
        <div className="card-rs p-3">
          <div className="text-[10px] label-up text-[var(--rs-text-3)]">MRR</div>
          <div className="text-lg font-bold mt-1" data-testid="rev-mrr">{fmtMoney(rev.mrr)}</div>
        </div>
        <div className="card-rs p-3">
          <div className="text-[10px] label-up text-[var(--rs-text-3)]">This month</div>
          <div className="text-lg font-bold mt-1" data-testid="rev-this-month">{fmtMoney(rev.thisMonthRevenue)}</div>
        </div>
        <div className="card-rs p-3">
          <div className="text-[10px] label-up text-[var(--rs-text-3)]">MoM change</div>
          <div className="text-lg font-bold mt-1" data-testid="rev-mom"
            style={{ color: mom == null ? undefined : mom >= 0 ? "#22C55E" : "rgb(252,165,165)" }}>
            {mom == null ? "—" : `${mom > 0 ? "+" : ""}${mom}%`}
          </div>
        </div>
      </section>

      {data.funnel && Object.keys(data.funnel).length > 0 && (
        <section className="card-rs p-4" data-testid="churn-funnel">
          <h2 className="text-[12px] label-up text-[var(--rs-text-3)] mb-3">Subscription funnel</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12px]">
            <div><div className="text-[10px] label-up text-[var(--rs-text-3)]">Pricing views</div><div className="text-base font-bold" data-testid="funnel-pricing-views">{data.funnel.pricingViews ?? 0}</div></div>
            <div><div className="text-[10px] label-up text-[var(--rs-text-3)]">Checkout attempts</div><div className="text-base font-bold" data-testid="funnel-checkouts">{data.funnel.checkoutAttempts ?? 0}</div></div>
            <div><div className="text-[10px] label-up text-[var(--rs-text-3)]">Trials started</div><div className="text-base font-bold" data-testid="funnel-trials">{data.funnel.trialsStarted ?? 0}</div></div>
            <div><div className="text-[10px] label-up text-[var(--rs-text-3)]">Trial conversion</div><div className="text-base font-bold" data-testid="funnel-conversion">{data.funnel.trialConversionRate ?? 0}%</div></div>
            <div><div className="text-[10px] label-up text-[var(--rs-text-3)]">Cancellations</div><div className="text-base font-bold" data-testid="funnel-cancellations">{data.funnel.cancellations ?? 0}</div></div>
            <div><div className="text-[10px] label-up text-[var(--rs-text-3)]">Failed payments</div><div className="text-base font-bold" data-testid="funnel-failed">{data.funnel.failedPayments ?? 0}</div></div>
            <div><div className="text-[10px] label-up text-[var(--rs-text-3)]">Founding claimed</div><div className="text-base font-bold" data-testid="funnel-founding-claimed">{data.funnel.founding500ClaimedPaddle ?? data.funnel.founding500Claimed ?? 0}</div></div>
            <div><div className="text-[10px] label-up text-[var(--rs-text-3)]">Founding remaining</div><div className="text-base font-bold" data-testid="funnel-founding-remaining">{data.funnel.founding500Remaining ?? "—"}</div></div>
          </div>
        </section>
      )}

      <button data-testid="churn-export-csv" onClick={downloadCsv}
        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md font-semibold text-[12px] border"
        style={{ borderColor: "var(--rs-border)", color: "var(--rs-amber)" }}>
        <Download className="w-3.5 h-3.5" /> Download CSV
      </button>

      <section className="card-rs p-4" data-testid="churn-top-reasons">
        <h2 className="text-[12px] label-up text-[var(--rs-text-3)] mb-3">Top reasons</h2>
        {data.topReasons.length === 0 && <p className="text-[12px] text-[var(--rs-text-3)]">No responses yet.</p>}
        <div className="space-y-2">
          {data.topReasons.map((r) => (
            <div key={r.reason}>
              <div className="flex justify-between text-[12px] mb-0.5">
                <span>{r.label}</span>
                <span className="text-[var(--rs-text-3)]">{r.count}</span>
              </div>
              <div className="h-2 rounded-full" style={{ background: "rgba(255,255,255,0.06)" }}>
                <div className="h-2 rounded-full" style={{ width: `${(r.count / maxReason) * 100}%`, background: "var(--rs-amber)" }} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="card-rs p-4" data-testid="churn-over-time">
        <h2 className="text-[12px] label-up text-[var(--rs-text-3)] mb-3">Responses over time</h2>
        {data.overTime.length === 0 && <p className="text-[12px] text-[var(--rs-text-3)]">No responses yet.</p>}
        <div className="flex items-end gap-2 h-24">
          {data.overTime.map((m) => (
            <div key={m.month} className="flex flex-col items-center gap-1 flex-1 min-w-0">
              <span className="text-[10px] text-[var(--rs-text-3)]">{m.count}</span>
              <div className="w-full rounded-t" style={{ height: `${(m.count / maxMonth) * 64}px`, minHeight: "4px", background: "var(--rs-amber)" }} />
              <span className="text-[10px] text-[var(--rs-text-3)] truncate w-full text-center">{m.month}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="card-rs p-4" data-testid="churn-responses">
        <h2 className="text-[12px] label-up text-[var(--rs-text-3)] mb-3">Individual responses</h2>
        {data.responses.length === 0 && <p className="text-[12px] text-[var(--rs-text-3)]">No responses yet.</p>}
        <div className="space-y-2">
          {data.responses.map((r, i) => (
            <div key={i} className="rounded-md border px-3 py-2 text-[12px]" style={{ borderColor: "var(--rs-border)" }} data-testid={`churn-response-${i}`}>
              <div className="flex flex-wrap justify-between gap-1">
                <span className="font-semibold">{r.label}</span>
                <span className="text-[var(--rs-text-3)]">{fmtDate(r.createdAt)}</span>
              </div>
              <div className="text-[var(--rs-text-3)]">
                {r.email || "unknown user"} · {r.mode === "immediate_refund" ? "refunded (3-day window)" : "end of period"}
              </div>
              {r.detail && <div className="mt-1 text-[var(--rs-text-2)]">"{r.detail}"</div>}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
